/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { DownloadIcon, ExternalLinkIcon, Cross2Icon, FileIcon } from '@radix-ui/react-icons';
import { Text } from '@/shared/ui';
import type { DocumentRowDocument } from '../document-row/DocumentRow';

/** Extensions whose bytes a browser renders on its own, by file name. */
const INLINE_EXTENSIONS = /\.(pdf|png|jpe?g|gif|webp|svg|txt)$/i;
const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|svg)$/i;

export interface DocumentViewerProps<TDocument extends DocumentRowDocument> {
  /** The document to show; `null` keeps the viewer closed. */
  doc: TDocument | null;
  onClose: () => void;
  /** i18n namespace to resolve labels from (e.g. 'features/clients'). */
  i18nNamespace: string;
  /** Key prefix within that namespace (e.g. 'profile.documents'). */
  i18nKeyPrefix: string;
}

/** True when the file can be framed rather than only downloaded. */
function isViewable(doc: DocumentRowDocument): boolean {
  const type = (doc as { type?: string }).type ?? '';
  if (type.startsWith('image/') || type === 'application/pdf' || type === 'text/plain') return true;
  return INLINE_EXTENSIONS.test(doc.filename);
}

/** True when the file is an image, which is shown as an `img` not a frame. */
function isImage(doc: DocumentRowDocument): boolean {
  const type = (doc as { type?: string }).type ?? '';
  return type.startsWith('image/') || IMAGE_EXTENSIONS.test(doc.filename);
}

/** The link that renders in place — the download link always saves. */
function viewHref(doc: DocumentRowDocument): string {
  return (doc as { view_url?: string }).view_url ?? doc.download_url ?? '';
}

/** A header control: square, borderless, inverse-on-dark. */
const HEADER_BUTTON =
  'flex h-(--space-10) w-(--space-10) shrink-0 items-center justify-center rounded-(--radius-md) ' +
  'border-0 bg-transparent text-(--text-inverse) cursor-pointer hover:bg-[rgba(255,255,255,.12)]';

/**
 * Full-screen viewer for a stored document.
 *
 * Opens over the detail panel rather than sending the file to the
 * downloads folder: reading a contract is a step inside the record, not a
 * trip out of the app. Mirrors the launch material viewer, minus the
 * paging and provider embeds those materials need.
 *
 * @param props - The document to show, a close handler and the i18n location.
 * @returns The rendered viewer, or null when no document is open.
 */
export const DocumentViewer = <TDocument extends DocumentRowDocument>({
  doc,
  onClose,
  i18nNamespace,
  i18nKeyPrefix,
}: DocumentViewerProps<TDocument>) => {
  const { t } = useTranslation(i18nNamespace);
  const [loading, setLoading] = useState(true);

  const documentId = doc?.document_id ?? null;

  /* A new document starts loading again — otherwise the spinner state of
     the previous one survives into the next. */
  useEffect(() => setLoading(true), [documentId]);

  /* A viewer that only answers the mouse is one a keyboard cannot leave. */
  const handleKey = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!documentId) return undefined;
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [documentId, handleKey]);

  if (!doc) return null;

  const href = viewHref(doc);
  const viewable = !!href && isViewable(doc);

  return createPortal(
    <div
      data-testid="document-viewer"
      role="dialog"
      aria-modal="true"
      aria-label={doc.filename}
      onClick={onClose}
      className="fixed inset-0 z-[10050] flex items-center justify-center bg-(--overlay-scrim-heavy) p-(--space-6)"
    >
      <div
        onClick={(event) => event.stopPropagation()}
        className="flex h-full w-full max-w-[1200px] flex-col overflow-hidden rounded-(--radius-lg) bg-(--surface-inverse) text-(--text-inverse) shadow-[var(--shadow-xl)]"
      >
        <div className="flex h-(--space-14) shrink-0 items-center gap-(--space-3) border-b border-[rgba(255,255,255,.12)] pl-(--space-5) pr-(--space-3)">
          <Text
            as="span"
            className="min-w-0 flex-1 truncate text-[length:var(--text-body)] [font-weight:var(--font-semibold)]"
          >
            {doc.filename}
          </Text>

          <a
            href={href || undefined}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t(`${i18nKeyPrefix}.openInNewTab`)}
            title={t(`${i18nKeyPrefix}.openInNewTab`)}
            className={HEADER_BUTTON}
          >
            <ExternalLinkIcon width={18} height={18} />
          </a>

          <a
            href={doc.download_url || undefined}
            download={doc.filename}
            aria-label={t(`${i18nKeyPrefix}.download`)}
            title={t(`${i18nKeyPrefix}.download`)}
            className={HEADER_BUTTON}
          >
            <DownloadIcon width={18} height={18} />
          </a>

          <button
            type="button"
            onClick={onClose}
            aria-label={t(`${i18nKeyPrefix}.close`)}
            title={t(`${i18nKeyPrefix}.close`)}
            className={HEADER_BUTTON}
          >
            <Cross2Icon width={18} height={18} />
          </button>
        </div>

        {!viewable && (
          <div className="flex flex-1 flex-col items-center justify-center gap-(--space-3) p-(--space-8) text-center">
            <span className="flex h-[64px] w-[64px] items-center justify-center rounded-(--radius-full) bg-[rgba(255,255,255,.1)]">
              <FileIcon width={28} height={28} />
            </span>
            <Text as="span" className="text-[length:var(--text-body-sm)] opacity-80">
              {t(`${i18nKeyPrefix}.previewUnavailable`)}
            </Text>
            {doc.download_url && (
              <a
                href={doc.download_url}
                download={doc.filename}
                className="mt-(--space-2) flex h-(--space-10) items-center rounded-(--radius-md) border border-[rgba(255,255,255,.3)] px-(--space-5) text-[length:var(--text-body-sm)] text-(--text-inverse) no-underline [font-weight:var(--font-semibold)]"
              >
                {t(`${i18nKeyPrefix}.download`)}
              </a>
            )}
          </div>
        )}

        {viewable && (
          <div className="relative flex min-h-0 flex-1 items-start justify-center overflow-auto p-(--space-6)">
            {isImage(doc) ? (
              <img
                src={href}
                alt={doc.filename}
                onLoad={() => setLoading(false)}
                className="m-auto max-w-full rounded-(--radius-sm) object-contain shadow-[var(--shadow-lg)]"
              />
            ) : (
              <iframe
                title={doc.filename}
                src={href}
                onLoad={() => setLoading(false)}
                className="m-auto h-full w-full rounded-(--radius-sm) border-0 bg-(--surface-primary) shadow-[var(--shadow-lg)]"
                style={{ display: loading ? 'none' : 'block' }}
              />
            )}

            {loading && (
              <Text
                as="span"
                className="m-auto text-[length:var(--text-small)] opacity-80"
              >
                {t(`${i18nKeyPrefix}.loadingDocument`)}
              </Text>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default DocumentViewer;
